import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import UsersTable from "../../components/users/UsersTable";
import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../components/ui/ModalProvider";
import userService from "../../services/userService";

export default function UsersOverview() {
  const { user, isManager, loading: authLoading, profileLoading } = useAuth();
  const navigate = useNavigate();
  const { confirm, alert } = useModal();

  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (!authLoading && !profileLoading && !isManager) {
      navigate("/automation-control-panel");
    }
  }, [authLoading, profileLoading, isManager, navigate]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await userService.fetchUsers();
    if (fetchError) {
      setError("Unable to load users at this time.");
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  };

  const handleRoleChange = async (userId, nextRole) => {
    const existing = users.find((u) => u.id === userId);
    if (!existing || existing.role === nextRole || !user?.id) return;
    const confirmed = await confirm(
      `Change ${existing.email}'s role to "${nextRole}"? This is immediate and audit-logged.`,
      { confirmText: "Change role" }
    );
    if (!confirmed) return;
    setProcessing({ userId, action: "role" });
    const { error: updateError } = await userService.updateUserRole(
      userId,
      nextRole,
      user.id
    );
    setProcessing(null);
    if (updateError) {
      alert("Failed to update role. Please try again.");
      return;
    }
    loadUsers();
  };

  const handleStatusToggle = async (userId, nextStatus) => {
    const existing = users.find((u) => u.id === userId);
    if (!existing || existing.isActive === nextStatus || !user?.id) return;
    const title = nextStatus ? "Enable account" : "Disable account";
    const confirmed = await confirm(
      `${title} for ${existing.email}? This will block automation and publishing activity.`,
      { confirmText: title }
    );
    if (!confirmed) return;
    setProcessing({ userId, action: "status" });
    const { error: updateError } = await userService.updateUserStatus(
      userId,
      nextStatus,
      user.id
    );
    setProcessing(null);
    if (updateError) {
      alert("Failed to update status. Please try again.");
      return;
    }
    loadUsers();
  };

  const handleDeleteUser = async (userId) => {
    const existing = users.find((u) => u.id === userId);
    if (!existing || !user?.id) return;
    const confirmed = await confirm(
      `Permanently delete ${existing.email}? This cannot be undone. All data, integrations, and access will be removed immediately.`,
      { confirmText: "Delete permanently" }
    );
    if (!confirmed) return;
    setProcessing({ userId, action: "delete" });
    const { error: deleteError } = await userService.deleteUser(userId);
    setProcessing(null);
    if (deleteError) {
      alert("Failed to delete user. Please try again.");
      return;
    }
    loadUsers();
  };

  const handleViewDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6 text-sm text-slate-500">
        Loading users…
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="main-content">
          <div className="w-full space-y-6">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
              <p className="text-sm text-slate-500">
                Manage all registered accounts, their role, status, and activity
                history.
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {error}
              </div>
            )}

            <UsersTable
              users={users}
              onRoleChange={handleRoleChange}
              onStatusToggle={handleStatusToggle}
              onViewDetails={handleViewDetails}
              onDelete={handleDeleteUser}
              processing={processing}
            />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
